// SMS Webhook Handler for processing delivery status updates
import supabase from '../supabaseClient';

/**
 * Handle SMS delivery status webhooks from various providers
 * @param {Object} webhookData - Data received from SMS provider webhook
 * @param {String} provider - SMS provider name ('twilio', 'aws-sns', etc.)
 * @returns {Promise<Boolean>} Success status
 */
export const handleSMSWebhook = async (webhookData, provider) => {
  try {
    console.log(`📡 Received ${provider} webhook:`, webhookData);

    let messageId, status, providerMessageId;

    // Parse webhook data based on provider
    switch (provider.toLowerCase()) {
      case 'twilio':
        messageId = webhookData.MessageSid;
        status = mapTwilioStatus(webhookData.MessageStatus);
        providerMessageId = webhookData.MessageSid;
        break;
        
      case 'aws-sns':
        messageId = webhookData.messageId;
        status = mapAWSStatus(webhookData.delivery?.dwellTimeMs ? 'delivered' : 'failed');
        providerMessageId = webhookData.messageId;
        break;
        
      default:
        console.error('Unsupported SMS provider:', provider);
        return false;
    }

    // Update message status in database
    const { error } = await supabase.rpc('update_sms_status', {
      message_id: messageId,
      new_status: status,
      provider_msg_id: providerMessageId,
      response_data: webhookData
    });

    if (error) {
      console.error('Error updating SMS status:', error);
      return false;
    }

    console.log(`✅ Updated SMS ${messageId} to status: ${status}`);
    return true;

  } catch (error) {
    console.error('Error processing SMS webhook:', error);
    return false;
  }
};

/**
 * Map Twilio status to our internal status
 */
const mapTwilioStatus = (twilioStatus) => {
  const statusMap = {
    'queued': 'queued',
    'sent': 'sent', 
    'delivered': 'delivered',
    'failed': 'failed',
    'undelivered': 'failed'
  };
  return statusMap[twilioStatus] || 'failed';
};

/**
 * Map AWS SNS status to our internal status
 */
const mapAWSStatus = (awsStatus) => {
  const statusMap = {
    'delivered': 'delivered',
    'failed': 'failed'
  };
  return statusMap[awsStatus] || 'failed';
};

/**
 * Send SMS via Twilio using Supabase Edge Function
 * @param {Array} phoneNumbers - Array of phone numbers
 * @param {String} message - Message content
 * @param {String} smsLogId - Database log ID to track
 * @returns {Promise<Object>} Result object
 */
export const sendSMSViaTwilio = async (phoneNumbers, message, smsLogId) => {
  try {
    console.log('📤 Sending SMS via Supabase Edge Function');
    console.log('📞 Phone Numbers:', phoneNumbers);
    console.log('💬 Message:', message);
    console.log('🆔 SMS Log ID:', smsLogId);

    // Call Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('send-twilio-sms', {
      body: {
        phoneNumbers,
        message,
        smsLogId,
      },
    });

    if (error) {
      console.error('Edge Function error:', error);
      return {
        success: false,
        error: error.message || 'Failed to invoke Edge Function',
        provider: 'twilio',
      };
    }

    if (!data) {
      return {
        success: false,
        error: 'No response from Edge Function',
        provider: 'twilio',
      };
    }

    // Edge Function returns the response directly
    if (data.success) {
      console.log('✅ SMS sent successfully:', data);
      return {
        success: true,
        provider: data.provider || 'twilio',
        results: data.results || [],
        successCount: data.successCount || 0,
        failureCount: data.failureCount || 0,
      };
    } else {
      console.error('Edge Function returned error:', data.error);
      return {
        success: false,
        error: data.error || 'Unknown error from Edge Function',
        provider: 'twilio',
      };
    }
  } catch (error) {
    console.error('Error sending SMS via Twilio:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred',
      provider: 'twilio',
    };
  }
};

/**
 * Update SMS status tracking
 * @param {String} smsLogId - Database log ID
 * @param {String} status - New status
 * @param {Object} additionalData - Additional tracking data
 */
export const updateSMSStatus = async (smsLogId, status, additionalData = {}) => {
  try {
    // First, get current delivery_attempts to increment it
    const { data: currentData, error: fetchError } = await supabase
      .from('sms_logs')
      .select('delivery_attempts')
      .eq('id', smsLogId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 is "not found" which is fine for new records
      throw fetchError;
    }

    const currentAttempts = currentData?.delivery_attempts || 0;

    const { error } = await supabase
      .from('sms_logs')
      .update({
        status,
        delivery_attempts: currentAttempts + 1,
        last_delivery_attempt: new Date().toISOString(),
        ...additionalData
      })
      .eq('id', smsLogId);

    if (error) throw error;
    
    console.log(`📊 Updated SMS ${smsLogId} status to: ${status}`);
    return true;
  } catch (error) {
    console.error('Error updating SMS status:', error);
    return false;
  }
};
